
import { Component, Vue, Watch } from 'vue-property-decorator'
import { useCultureSubsidyStore } from '@/stores/cultureSubsidy'
import { SettingsPayload, SubsidyStatus } from '../types'

interface SettingsForm {
  ratePercent: number
  orderCapping: number
  perProductValueMode: boolean
  annualBeneficiaryCapping: number
  parameterizedBudget: number
  fallbackLoyalty: boolean
  fallbackFree: boolean
  fallbackRecycling: boolean
  status: SubsidyStatus
}

@Component({ name: 'SettingsPanel' })
export default class extends Vue {
  store = useCultureSubsidyStore()
  simulating = false
  sim = {
    productId: '',
    beneficiaryId: '' as string,
    orderAmount: 0
  }

  form: SettingsForm = this.emptyForm()

  get settings() {
    return this.store.settings
  }

  get eligibleProducts() {
    return this.store.products.filter(product => product.eligible)
  }

  get result() {
    return this.store.simulation
  }

  get dirty(): boolean {
    if (!this.settings) return false
    const current = this.payloadFromForm()
    return JSON.stringify(current) !== JSON.stringify(this.payloadFromSettings())
  }

  @Watch('settings', { immediate: true })
  onSettings() {
    this.resetForm()
    if (!this.sim.productId && this.eligibleProducts[0]) {
      this.sim.productId = this.eligibleProducts[0].id
      this.sim.orderAmount = this.eligibleProducts[0].csePrice
    }
  }

  emptyForm(): SettingsForm {
    return {
      ratePercent: 30,
      orderCapping: 0,
      perProductValueMode: false,
      annualBeneficiaryCapping: 0,
      parameterizedBudget: 0,
      fallbackLoyalty: false,
      fallbackFree: false,
      fallbackRecycling: false,
      status: 'active'
    }
  }

  resetForm(): void {
    if (!this.settings) return
    this.form = {
      ratePercent: Math.round(this.settings.standardRate * 1000) / 10,
      orderCapping: this.settings.orderCapping,
      perProductValueMode: this.settings.perProductValueMode,
      annualBeneficiaryCapping: this.settings.annualBeneficiaryCapping,
      parameterizedBudget: this.settings.parameterizedBudget,
      fallbackLoyalty: this.settings.fallbackLoyalty,
      fallbackFree: this.settings.fallbackFree,
      fallbackRecycling: this.settings.fallbackRecycling,
      status: this.settings.status === 'blocked' ? 'blocked' : this.settings.status
    }
  }

  payloadFromForm(): SettingsPayload {
    return {
      status: this.form.status,
      standardRate: this.form.ratePercent / 100,
      perProductValueMode: this.form.perProductValueMode,
      orderCapping: this.form.orderCapping,
      annualBeneficiaryCapping: this.form.annualBeneficiaryCapping,
      parameterizedBudget: this.form.parameterizedBudget,
      fallbackLoyalty: this.form.fallbackLoyalty,
      fallbackFree: this.form.fallbackFree,
      fallbackRecycling: this.form.fallbackRecycling
    }
  }

  payloadFromSettings(): SettingsPayload {
    if (!this.settings) return {}
    return {
      status: this.settings.status,
      standardRate: this.settings.standardRate,
      perProductValueMode: this.settings.perProductValueMode,
      orderCapping: this.settings.orderCapping,
      annualBeneficiaryCapping: this.settings.annualBeneficiaryCapping,
      parameterizedBudget: this.settings.parameterizedBudget,
      fallbackLoyalty: this.settings.fallbackLoyalty,
      fallbackFree: this.settings.fallbackFree,
      fallbackRecycling: this.settings.fallbackRecycling
    }
  }

  async save(): Promise<void> {
    try {
      await this.store.saveSettings(this.payloadFromForm())
      this.$message.success(this.$t('cultureSubsidy.settings.saved') as string)
    } catch (error) {
      this.$message.error((error as Error).message)
    }
  }

  onProductChange(productId: string): void {
    const product = this.store.products.find(item => item.id === productId)
    if (product) this.sim.orderAmount = product.csePrice
  }

  async runSimulation(): Promise<void> {
    this.simulating = true
    try {
      await this.store.simulate({
        productId: this.sim.productId,
        beneficiaryId: this.sim.beneficiaryId || null,
        orderAmount: this.sim.orderAmount
      })
    } catch (error) {
      this.$message.error((error as Error).message)
    } finally {
      this.simulating = false
    }
  }
}
